/**
 * Generic runtime feature flags for build-ahead studio features.
 *
 * Some features are wired end-to-end in the front-end before their backend / rollout is
 * ready (e.g. activating a suggested campaign — no provider action stands behind it yet).
 * Rather than cut them, we ship them gated: most surfaces show a frosted
 * "Unlocking soon" control (see `components/LockVeil`), others simply hide the control
 * outright (a veiled-but-dead button would mislead) — both flipped live by the runtime
 * reveal flag so QA can preview them. Flip a feature's `enabled` flag below to roll it out;
 * every surface that gates on it reacts in one place.
 *
 * This is the SINGLE SOURCE OF TRUTH — gate UI off {@link isFeatureEnabled} /
 * {@link isFeatureLocked}, never a hard-coded list. It mirrors `config/acquisitionChannels`
 * exactly, generalised to any feature id, with the same resolution precedence (highest first):
 *  1. the `show_in_progress` reveal flag unlocks EVERY gated feature (see `hooks/useShowInProgress`);
 *  2. an optional `window.config.featureFlagsEnabled` (comma-separated id list, e.g.
 *     `"campaign-activate"`) overrides the set per-environment from the deploy config;
 *  3. otherwise the registry's `enabled` flags (the release default) win.
 *
 * Resolved fresh on every call so a devtools flip is honoured immediately; React surfaces stay
 * in sync via {@link useFeatureGate} (it re-renders on the flip). The `window.config` key is
 * intentionally NOT registered in the deploy Helm `config.yaml` yet — wire it there to make the
 * set per-environment configurable; until then the code defaults below win.
 */
import { SHOW_IN_PROGRESS_KEY, REVEAL_CHANGE_EVENT } from '../hooks/useShowInProgress';

/** Build-ahead feature ids gated by this module. Add a row to {@link REGISTRY} to ship one. */
export type FeatureFlagKey =
  | 'campaign-activate'
  | 'campaign-pause'
  | 'channel-campaign-toggle'
  | 'lumio-suggestions'
  | 'promotion-performance'
  | 'deploy-progress'
  | 'creatives'
  | 'creatives-carousels'
  | 'creatives-above-the-fold'
  | 'creatives-assets'
  | 'mailbox'
  | 'mailbox-support'
  | 'secure-brand'
  | 'creatives-games'
  | 'owner-game-lists'
  | 'list-name-languages'
  | 'creatives-video'
  | 'creatives-banner-kits'
  | 'creatives-playables'
  | 'creatives-html5-banners'
  | 'creatives-landing-pages'
  | 'creatives-kit-layers'
  | 'creatives-dev-debug'
  | 'affiliate-management'
  | 'art-style'
  | 'website-hub'
  | 'website-seo'
  | 'list-wizard-v2'
  | 'market-lists'
  | 'website-analytics'
  | 'country-filter'
  | 'ftux'
  | 'owner-notifications';

type FeatureFlagEntry = {
  /** Stable id used by callers + the `window.config.featureFlagsEnabled` override list. */
  id: FeatureFlagKey;
  /** Whether the feature is live in this release. Flip to roll it out. */
  enabled: boolean;
  /**
   * When true, the blanket `show_in_progress` ("777") reveal does NOT unlock this feature — only its
   * OWN per-feature reveal key does. For a not-yet-ready sub-feature that must stay hidden even while
   * every other in-progress placeholder is revealed (e.g. a BE-limited section that would show broken).
   */
  blanketExempt?: boolean;
};

/** The release default. Flip `enabled` to roll a feature out; nothing else changes. */
const REGISTRY: readonly FeatureFlagEntry[] = [
  // Activate a suggested campaign / promotion from its detail page: the "Choose campaign style"
  // step and the "Activate now for free" button. Both are build-ahead: activating writes a LOCAL
  // status override (utils/campaign-status) and the chosen style goes nowhere, so an owner could
  // move a campaign to Running in their browser with no provider action behind it. It ships
  // hidden until the backend exposes a real activate action. No acquisition campaign reaches
  // this page from the API today (the Ad Service yields no suggestions), so the control is
  // reachable only from the Operations promotions. QA-revealable via `show_in_progress`.
  { id: 'campaign-activate', enabled: false },
  // Pause / resume a running campaign or promotion from its detail page. The control writes a
  // LOCAL status override (utils/campaign-status), not a backed provider action yet, so it ships
  // hidden — no veil, just absent — until the backend exposes real pause/resume; the detail page
  // still reflects whatever status (running / paused / ended) the data reports. QA-revealable via
  // the runtime `show_in_progress` flag.
  { id: 'campaign-pause', enabled: false },
  // Enable / disable a campaign within a channel, from the channel detail page. Same build-ahead
  // story (the toggle is local UI intent, not a backed action yet), so the per-campaign toggle is
  // hidden until the backend lands it. QA-revealable via the runtime `show_in_progress` flag.
  { id: 'channel-campaign-toggle', enabled: false },
  // Lumio welcome suggestion cards (HOT TOPICS / DEEP DIVE) on the empty conversation state. The
  // greeting always shows; the suggestion sections ship hidden for now while the topic set is
  // still being shaped, leaving just the "Hello" welcome. QA-revealable via `show_in_progress`.
  { id: 'lumio-suggestions', enabled: false },
  // Operations promotion "Campaign Performance" KPIs (Activated players / Turnover / Deposits /
  // GGR) on the promotion detail page. The CRM Events API does not expose per-promotion performance
  // figures yet, so the grid is still mock — it ships frosted under an "Unlocking soon" veil rather
  // than showing invented numbers, until the backend delivers real figures. Acquisition campaign
  // performance is unaffected (it is wired to Ad Service + Stats). QA-revealable via `show_in_progress`.
  { id: 'promotion-performance', enabled: false },
  // Reveals the LEGACY "Crafting {brand}" deploy strip (BrandDeploymentStripLegacy, the
  // pre-redesign fanned/tilted version) stacked BELOW the current one on the account home, for
  // comparison. The redesigned strip is always visible; this flag only surfaces the old variant
  // via the runtime `show_in_progress` reveal (the Lumio "777" easter egg).
  { id: 'deploy-progress', enabled: false },
  // The Website Creatives ENTRYPOINT (the /account/website/creatives route + its Website tab) and its always-on
  // "Website Creatives" banner library. Now GENERALLY AVAILABLE: rolled out to everyone, no longer
  // held behind the "777" / "777creatives" reveal. The gate entry is kept as the single-flip rollback
  // switch (set `enabled: false` to re-hide it behind the reveal); the sub-sections keep their own
  // tiers below.
  { id: 'creatives', enabled: true },
  // The curated Carousels (Homepage / Casino / Sports) that pick + order the pool's banners. Part of
  // the base Creatives experience, so it ships with the entrypoint: now GENERALLY AVAILABLE for
  // everyone. Kept in the registry as the single-flip rollback switch (set `enabled: false` to re-gate
  // it behind the "777" reveal).
  { id: 'creatives-carousels', enabled: true },
  // Website Creatives > Above the fold (the brand's hero, Casino/Sports cards and register image, with per-image
  // redraws): its tab is hidden for now. Off, the tab never shows, a ?view=above-the-fold link lands on the pool and
  // the page does not read the above the fold at all. `blanketExempt` and in no reveal list, so neither "777" nor
  // "777creatives" brings it back; `window.config.featureFlagsEnabled` still can. Flip `enabled` to show it again.
  { id: 'creatives-above-the-fold', enabled: false, blanketExempt: true },
  // Marketing: the "Marketing" rail entry, the `/account/marketing-assets` hub and every Marketing area route. Now
  // GENERALLY AVAILABLE: rolled out to everyone, no longer held behind the "777creatives" reveal. Kept in the registry
  // as the single-flip rollback switch (set `enabled: false` to re-hide it behind the plain "777" reveal). Each area
  // below has its own row too.
  { id: 'creatives-assets', enabled: true },
  // Settings → Mailbox: the one brand mail section (real mailboxes on the brand domain, a card grid
  // with a detail page and a billing lifecycle). WHETHER it is shown is this flag; WHERE its data
  // comes from is separate: an environment whose config carries `owsMailApiUrl` calls ows-mail for
  // real (creates and deletes real mailboxes), one without it says the service is not
  // available there (the fixtures only when a developer chose them; see `config/mailboxSource.ts`). Now GENERALLY
  // AVAILABLE: on for every owner, no longer held behind the plain "777" reveal. A brand domain ows-mail says can
  // never have mail still drops it (`useMailboxEligibility`), and support@ stays behind its own "777supportmailbox".
  // Both the Settings row and the section gate on this key via `useFeatureGate`. Kept as the single-flip rollback
  // switch (set `enabled: false` to re-hide it behind the plain "777" reveal).
  { id: 'mailbox', enabled: true },
  // Settings → Mailbox: the brand's system mailbox (`support@`, where the casino's CRM mail arrives) as a card in the
  // grid, in the heading count and reachable on its own mailbox page. Operators do not see it. Only its own
  // "777supportmailbox" code reveals it (blanketExempt: plain "777" must not). The section itself still needs `mailbox`.
  { id: 'mailbox-support', enabled: false, blanketExempt: true },
  // Settings → Secure your brand: the owner signs the License Holder Declaration through DocuSign. The section
  // calls the real spark-esign, so it stays off everywhere until launch, dev builds included: only the plain "777"
  // reveal shows it (not blanketExempt, so QA reveals it with the plain "777", Alex, 2026-10-09). Launch is flipping
  // this row to `enabled: true`, and only once the production DocuSign account and plan, spark-esign in production
  // with its `spark_esign_api_url`, and the production test envelope are in place (umbrella checklist items 5 and
  // 11). Both the Settings row and the section route gate on this key via `useFeatureGate`.
  { id: 'secure-brand', enabled: false },
  // Settings > Website Settings > Country access: the owner switches off the countries that cannot use the casino
  // (owner-hub `/casino-instance/country-filter`, stored in the brand's ACL tenant). Held back because no service
  // applies the setting yet (Thoth `automation-micros-wl_owner_hub/country_filter.md`): the card would store a filter
  // that has no effect. Flip to `true` once a service enforces it.
  { id: 'country-filter', enabled: false },
  // The Website "Game Lists" tab (SPB-327): reorder a site game list and preview it on the live site. Now
  // GENERALLY AVAILABLE: the owner-facing game-list publish path is live (a list publishes as the brand's
  // tenant data source through spark-creatives, exactly like a banner), so it rolls out to everyone. Where
  // that service or its game-list routes are unreachable the page self-degrades (Publish stays off and says
  // so). Kept in the registry as the single-flip rollback switch (set `enabled: false` to re-hide it behind
  // the "777" reveal).
  { id: 'creatives-games', enabled: true },
  // Stats > Website: the brand's website and landing-page visitor stats (spark-creatives website-analytics routes,
  // read from the analytics service), plus the visits figure on Home. Now GENERALLY AVAILABLE. In a cluster whose
  // analytics token is not wired the routes answer configured=false. Kept as the single-flip rollback switch (set
  // `enabled: false` to re-hide it behind the plain "777" reveal).
  { id: 'website-analytics', enabled: true },
  // The brand's OWN game lists inside the Game Lists tab: the lists an owner creates, edits, deletes and
  // publishes (stored in spark-creatives' owner-game-lists), their chips, the list editor, Add list in Manage
  // lists, and their place in the preview and the page's arrangement. Generally available (needs spark-creatives'
  // owner_game_lists migrations applied). Kept as the single-flip rollback switch: `enabled: false` hides it
  // behind the "777" reveal, and the tab then works as it did before own lists existed (no request, no own-list UI).
  { id: 'owner-game-lists', enabled: true },
  // An own list's name in every language of the brand's website: translated on create and rename, and edited one
  // language at a time from the chips under its heading's field and on the list editor's Name step (EN is the name
  // itself). On since 2026-11-03, turned on for test; the code default is every environment's (no deploy list pins
  // flags), so production gets it with its next release. `enabled: false` is the rollback: it goes back behind the
  // "777" reveal, nothing is translated and neither offers languages; names a list was given meanwhile keep reaching the
  // site until it is renamed.
  { id: 'list-name-languages', enabled: true },
  // Marketing > Video Ads (the video-generation vertical slice, v1 and v2.0). GENERALLY AVAILABLE with the rest of
  // Marketing. Its hub card still waits for the `video` price row (spark-creatives' `/credits/pricing` actions table,
  // see `visibleMarketingAreas`), so the cost chip and the pre-flight credits gate always have a price to check
  // against. Rollback switch: `enabled: false` re-hides it behind the plain "777" reveal.
  { id: 'creatives-video', enabled: true },
  // Marketing > Image Banners (the banner kits): one AI-rendered ad across every ad-network, social and CRM size. While
  // it is on, the older Image Ads tab is hidden. GENERALLY AVAILABLE with the rest of Marketing. Rollback switch:
  // `enabled: false` re-hides it behind the plain "777" reveal.
  { id: 'creatives-banner-kits', enabled: true },
  // Marketing > Mini-games: branded HTML5 playable ads (template, AI art, sandboxed preview, export profiles).
  // GENERALLY AVAILABLE with the rest of Marketing. Same rollback switch.
  { id: 'creatives-playables', enabled: true },
  // Marketing > HTML5 Banners: non-gamified animated banners with live text (the playable stack's `banner` templates)
  // and animated Image Banners kits, delivered as clickTAG ZIPs. GENERALLY AVAILABLE with the rest of Marketing. Same
  // rollback switch.
  { id: 'creatives-html5-banners', enabled: true },
  // Marketing > Landing Pages: the brand's live landing pages on its own lp. domain, and a studio that saves drafts,
  // runs credit-charging generations (copy, scene, lettering) and publishes pages to the brand's public lp. domain.
  // GENERALLY AVAILABLE with the rest of Marketing. Same rollback switch.
  { id: 'creatives-landing-pages', enabled: true },
  // The banner-kit layer editor (Seedream layer decomposition, move/scale/hide, text edits, flatten). Also
  // gated by the backend's `features.layers` capability; this flag keeps the UI off even where the backend
  // has it on. Still held back: `blanketExempt`, so plain "777" does not reveal it; only the "777creatives" key
  // does (see CREATIVES_REVEAL_KEY below).
  { id: 'creatives-kit-layers', enabled: false, blanketExempt: true },
  // Raw generation controls in the marketing-asset composers (the op checkboxes, the scene layers and cast): the
  // owner never sees them, the composers run on Lumio's defaults. Only its own "777devdebug" code reveals them
  // (blanketExempt: plain "777" must not).
  { id: 'creatives-dev-debug', enabled: false, blanketExempt: true },
  // The FULL affiliate management UX (the Affiliates directory tab + create entrypoint, the Campaigns tab, and the
  // Reports dashboard), on top of the Affiliates MVP (this-month overview + a summary card with counts and the Manage
  // Affiliates SSO into the external console), which is never gated. Now GENERALLY AVAILABLE: rolled out to everyone,
  // and the "777affiliate" code that used to reveal it is gone. Kept as the single-flip rollback switch (set
  // `enabled: false` to re-hide the tabbed management behind the plain "777" reveal).
  { id: 'affiliate-management', enabled: true },
  // The Website "Art Style" area (the /account/website/art-style route, its tab and its hub card). Now GENERALLY
  // AVAILABLE. Kept as the single-flip rollback switch (set `enabled: false` to re-hide it behind the plain "777"
  // reveal).
  { id: 'art-style', enabled: true },
  // The Website hub: `/account/website` as one card per Website area, each area page headed by a back button to it
  // instead of the tab row. Now GENERALLY AVAILABLE. Kept as the single-flip rollback switch: `enabled: false` brings
  // back the section as it was before the hub (`/account/website` lands on the first available tab and every area page
  // shows the tab row), with the hub behind the plain "777" reveal.
  { id: 'website-hub', enabled: true },
  // The Website "SEO" area (the /account/website/seo route, its tab and its hub card): the owner's meta and footer text
  // per route, which ship together. Off until spark-creatives' `site-seo` routes and the casino front's meta escaping
  // are live; QA reveals it through the plain "777". At release it flips to true and stays as the rollback switch.
  { id: 'website-seo', enabled: false },
  // The list wizard's second look, from the Claude Design "Add list" prototype: its steps as pills with a slide between
  // them, and the automatic Games step as three filter cards that tighten once something is picked. Add and Edit alike.
  // Now GENERALLY AVAILABLE. Kept as the single-flip rollback switch: `enabled: false` brings back the first look, with
  // the second behind the plain "777" reveal.
  { id: 'list-wizard-v2', enabled: true },
  // FTUX - Getting Started (`features/ftux`): Lumio's hello on Home, the menu's map, then the brand pieces one at a
  // time from Home's Spotlight, the owner's run kept in their preferences (key `ftux`) and its moments sent to
  // ows-uxo. GENERALLY AVAILABLE (the user, 2026-10-22): every owner gets it, the "777ftux" code gone. Kept as the
  // single-flip rollback switch (`enabled: false` hides it again; `blanketExempt`, so plain "777" never brings it back,
  // only "777ftuxqa" does). "777ftuxqa" (FTUX_QA_REVEAL_KEY) adds the testers' tools: the QA strip in Lumio's chat. It
  // never mounts while a support agent is signed in as the owner.
  { id: 'ftux', enabled: true, blanketExempt: true },
  // Start a list from the market: a third way on the list wizard's Method step, from the game collections the Casino
  // Catalogue API publishes for the brand's market (spark-creatives GET …/market-collections). Off until that route
  // and its token are deployed; QA-revealable via the plain "777".
  { id: 'market-lists', enabled: false },
  // The owner's notifications from Prisma (the partner's CRM): the top bar's bell, its ring and its list, and every
  // read behind them (owner-hub `/user/notifications` on load, on a tab return, on a socket reconnect, and a pushed
  // one). Off until we decide the whole system is ready (Prisma, Owner Hub's inbox and this bell; the user, 2026-10-29):
  // off, nothing shows and nothing is requested. QA reveals it through the plain "777"; `featureFlagsEnabled` can turn
  // it on per environment. At release it flips to true and stays as the rollback switch.
  { id: 'owner-notifications', enabled: false },
];

/**
 * Per-feature reveal keys: localStorage flags that unlock ONE feature (or one
 * feature and its sub-sections), as against the blanket `show_in_progress`
 * which unlocks them all.
 *
 * Same storage-plus-event shape as `show_in_progress`, and deliberately on the
 * same notification channel ({@link REVEAL_CHANGE_EVENT}) — a reveal that no
 * subscriber hears about is a flag that only works after a reload, which is the
 * one property this module promises it does not have.
 *
 * The "777creatives" code's key. Website Creatives, its Carousels and all of Marketing are generally
 * available (their rollback switches re-hide them behind the plain "777"), so its one remaining job is
 * the still-held-back banner-kit layer editor (`creatives-kit-layers`), for testing.
 */
export const CREATIVES_REVEAL_KEY = 'show_creatives';

/** The "777devdebug" code's key: reveals the composers' raw generation controls (`creatives-dev-debug`). */
export const DEVDEBUG_REVEAL_KEY = 'show_devdebug';

/**
 * The "777ftuxqa" code's key: FTUX - Getting Started's testers' tools, the QA strip in Lumio's chat and Lumio's
 * surfaces lifted over the flow while it holds the screen. It turns `ftux` on too, for when the flag is rolled back.
 */
export const FTUX_QA_REVEAL_KEY = 'show_ftux_qa';

/** The "777supportmailbox" code's key: shows the brand's support@ mailbox in Settings → Mailbox (`mailbox-support`). */
export const SUPPORT_MAILBOX_REVEAL_KEY = 'show_support_mailbox';

/** A single storage key may reveal SEVERAL features together (e.g. the entrypoint + its sub-sections). */
const REVEAL_KEYS: Readonly<Record<string, readonly FeatureFlagKey[]>> = {
  [CREATIVES_REVEAL_KEY]: ['creatives-kit-layers'],
  [DEVDEBUG_REVEAL_KEY]: ['creatives-dev-debug'],
  [FTUX_QA_REVEAL_KEY]: ['ftux'],
  [SUPPORT_MAILBOX_REVEAL_KEY]: ['mailbox-support'],
};

const readFlag = (key: string): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(key) === 'true';
  } catch {
    // Private mode / blocked storage → keep the release defaults.
    return false;
  }
};

/** The ids unlocked by their own reveal key right now (one key may unlock several). */
const revealedFeatures = (): FeatureFlagKey[] =>
  Object.entries(REVEAL_KEYS)
    .filter(([key]) => readFlag(key))
    .flatMap(([, ids]) => ids);

/** Read a per-feature reveal imperatively — for the Lumio code that TOGGLES it. */
export const getFeatureReveal = (storageKey: string): boolean => readFlag(storageKey);

/** Flip a per-feature reveal and tell this tab, the way `setShowInProgress` does. */
export const setFeatureReveal = (storageKey: string, on: boolean): void => {
  try {
    window.localStorage.setItem(storageKey, on ? 'true' : 'false');
    window.dispatchEvent(new Event(REVEAL_CHANGE_EVENT));
  } catch {
    // ignore unavailable storage
  }
};

/**
 * A value that changes whenever ANY reveal flag does, for `useSyncExternalStore`.
 *
 * `useShowInProgress` alone cannot drive {@link useFeatureGate} any more: its
 * snapshot is the one flag, so flipping a per-feature reveal fires the event,
 * the store re-reads, the snapshot compares equal and React skips the render —
 * the flag would appear not to work until the next reload.
 */
export const featureRevealSnapshot = (): string =>
  `${readFlag(SHOW_IN_PROGRESS_KEY) ? '1' : '0'}:${Object.keys(REVEAL_KEYS).filter(readFlag).sort().join(',')}`;

/** Which ids this registry governs (fast membership test). */
const FEATURE_IDS = new Set<string>(REGISTRY.map((entry) => entry.id));

/**
 * Whether the runtime `show_in_progress` reveal flag is on (see `hooks/useShowInProgress`).
 * When set, every gated feature is treated as live so QA can preview the not-yet-shipped ones
 * without a redeploy. Read fresh on every call (never cached) so a devtools flip is honoured
 * immediately; React surfaces re-render via {@link useFeatureGate}.
 */
const revealAll = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(SHOW_IN_PROGRESS_KEY) === 'true';
  } catch {
    // Private mode / blocked storage → keep the release defaults.
    return false;
  }
};

/**
 * Resolve the live set of feature ids, fresh on every call so runtime flips are honoured:
 *  1. the `show_in_progress` reveal flag unlocks every feature (highest precedence);
 *  2. an optional `window.config.featureFlagsEnabled` (comma-separated id list) overrides the
 *     set per-environment from the deploy config;
 *  3. otherwise the registry's `enabled` flags (the release default) win.
 */
const resolveEnabled = (): ReadonlySet<string> => {
  // Per-feature reveals (the "777xxx" keys) always apply first — including a `blanketExempt` feature
  // whose OWN key is on, which is the only way it is ever revealed.
  const revealed = new Set<string>(revealedFeatures());

  // The blanket "777" reveal unlocks every feature EXCEPT the `blanketExempt` ones (those stay hidden
  // until their own key is entered, already folded in above).
  if (revealAll()) {
    for (const entry of REGISTRY) {
      if (!entry.blanketExempt) revealed.add(entry.id);
    }
    return revealed;
  }

  const raw =
    typeof window !== 'undefined'
      ? (window.config as unknown as { featureFlagsEnabled?: string } | undefined)?.featureFlagsEnabled
      : undefined;

  const base =
    typeof raw === 'string' && raw.trim() !== ''
      ? ((): string[] => {
          const wanted = new Set(raw.split(',').map((part) => part.trim()));
          return REGISTRY.filter((entry) => wanted.has(entry.id)).map((entry) => entry.id);
        })()
      : REGISTRY.filter((entry) => entry.enabled).map((entry) => entry.id);

  // Unioned at the END, never as an early return: the `featureFlagsEnabled`
  // branch REPLACES the set, so a per-feature reveal checked before it would be
  // silently dropped in exactly the environments that pin the list.
  return new Set([...base, ...revealed]);
};

/**
 * Whether a build-ahead feature is live in this release. Ids NOT in the registry are not
 * governed and resolve to `true`, so unrelated UI is never accidentally locked. Resolves at
 * call time (honours the runtime reveal flag); React callers wrap it in {@link useFeatureGate}
 * to re-render on a flip.
 */
export const isFeatureEnabled = (key: string | undefined): boolean => {
  if (!key) return true;
  if (!FEATURE_IDS.has(key)) return true;
  return resolveEnabled().has(key);
};

/** Whether a build-ahead feature is gated off in this release (inverse of the above). */
export const isFeatureLocked = (key: string | undefined): boolean => !isFeatureEnabled(key);
